<template>
  <div
    class="fixed inset-0 z-50 bg-gray-900/60 flex items-stretch sm:justify-end transition-opacity duration-300"
    :class="isVisible ? 'opacity-100' : 'opacity-0'"
    @click.self="handleClose"
  >
    <div
      class="w-full sm:w-400 bg-white transition-transform duration-300 ease-out h-full shadow-2xl flex flex-col"
      :class="
        isVisible
          ? 'translate-y-0 sm:translate-x-0'
          : 'translate-y-full sm:translate-y-0 sm:translate-x-full'
      "
    >
      <div
        class="p-5 border-b border-gray-150 flex justify-between items-center"
      >
        <h2
          class="text-2xl font-bold uppercase tracking-tighter text-gray-950 leading-none"
        >
          KONTAKT
        </h2>

        <button
          @click="handleClose"
          class="p-2 -mr-2 text-gray-950 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-8 h-8"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div class="p-6 overflow-y-auto">
        <p class="text-sm font-bold text-gray-950">Hast Du Fragen?</p>

        <div class="mt-6 space-y-4">
          <a
            v-if="hasPhone"
            :href="phoneLink"
            class="group flex items-center gap-5 p-5 rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-all active:scale-95"
          >
            <div class="flex-shrink-0 text-gray-900">
              <svg-icon name="phone" class="w-8 h-8" />
            </div>
            <div>
              <span class="block text-lg font-bold text-gray-950">
                Ruf uns an
              </span>
              <span class="block text-sm text-gray-950 font-normal">
                Mo-Do 9-18 Uhr, Fr 9-17h
              </span>
            </div>
          </a>

          <a
            v-if="hasWhatsapp"
            :href="whatsappLink"
            target="_blank"
            rel="noopener noreferrer"
            class="group flex items-center gap-5 p-5 rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-all active:scale-95"
          >
            <div class="flex-shrink-0 text-gray-900">
              <svg-icon name="whatsapp" class="w-8 h-8" />
            </div>
            <div>
              <span class="block text-lg font-bold text-gray-950">
                Schreib uns an
              </span>
              <span class="block text-sm text-gray-950 font-normal">
                Mo-Do 9-18 Uhr, Fr 9-17h
              </span>
            </div>
          </a>

          <a
            v-if="hasEmail"
            :href="emailLink"
            class="group flex items-center gap-5 p-5 rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-all active:scale-95"
          >
            <div class="flex-shrink-0 text-gray-900">
              <svg-icon name="message-bubble" class="w-8 h-8" />
            </div>
            <div class="min-w-0 flex-1">
              <span class="block text-lg font-bold text-gray-950">
                Schreib uns an
              </span>
              <span
                class="block text-sm text-gray-950 font-normal underline underline-offset-4 decoration-1 truncate"
              >
                {{ contactInfo.email }}
              </span>
            </div>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import SvgIcon from '@/components/SvgIcon.vue'
import unitStore from '@/stores/unit.store'

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '')
}

const emit = defineEmits(['close'])
const isVisible = ref(false)

onMounted(() => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      isVisible.value = true
    })
  })
})

const handleClose = () => {
  isVisible.value = false
  setTimeout(() => emit('close'), 300)
}

const campusContact = computed(() => unitStore.getCampusContact())
const advisorContact = computed(() => unitStore.getAdvisorContact())
const cscContact = computed(() => unitStore.getCscContact())

const contactInfo = computed(() => ({
  phone:
    campusContact.value?.phoneNumber ||
    advisorContact.value?.phone ||
    cscContact.value?.phone ||
    '',
  email:
    campusContact.value?.emailAddress ||
    advisorContact.value?.email ||
    cscContact.value?.email ||
    '',
  whatsapp:
    advisorContact.value?.whatsapp ||
    campusContact.value?.whatsappLink ||
    cscContact.value?.whatsapp ||
    '',
}))

const hasPhone = computed(() => digitsOnly(contactInfo.value.phone).length > 0)
const hasEmail = computed(() => Boolean(contactInfo.value.email))
const hasWhatsapp = computed(
  () => digitsOnly(contactInfo.value.whatsapp).length > 0,
)

const phoneLink = computed(() => {
  const d = digitsOnly(contactInfo.value.phone)
  if (!d) return '#'
  return `tel:${d}`
})

const emailLink = computed(() => {
  if (!contactInfo.value.email) return '#'
  return `mailto:${contactInfo.value.email}`
})

const whatsappLink = computed(() => {
  const rawWhatsApp = contactInfo.value.whatsapp.trim()
  if (!rawWhatsApp) return '#'
  const d = digitsOnly(rawWhatsApp)
  if (!d) return '#'
  if (/^https?:\/\//i.test(rawWhatsApp)) {
    return rawWhatsApp
  }
  return `https://wa.me/${d}`
})
</script>
